// `configureEvidenceData` — the injected-config seam, born fail-loud
// (`configureCodingData` / `configureDictationData` / `configureWorkspaceData`
// shape). Wired app-side in `src/utils/evidence-data-wiring.ts`.
//
// ONE INPUT, AND THE OMISSION IS THE INTERESTING PART. Two inputs were
// anticipated — `useAuthToken` and `queryClient`. Only the second is here, and
// the first is absent as a MEASUREMENT rather than as scheduling:
//
//   * `queryClient` (severance cohort S3) is unavoidable. Two of the carved
//     surface's exports — `getCachedEvidenceConfig` and
//     `invalidateEvidenceConfig` — are MODULE-SCOPE cache commands, not hooks.
//     They ran against the `@/utils/reactQueryClient` singleton, and they are
//     called from outside React (the team-shortcut write fires the invalidator
//     from a mutation callback). There is no `useQueryClient()` in reach, so the
//     app's singleton crosses. It is the same instance the provider is seeded
//     with, so cache identity and timing are unchanged.
//
//   * `useAuthToken` (severance cohort S2) is NOT here, because after the
//     `enabled` collapse nothing package-side reads a token. All four carved
//     reads gated on `!!token && <app fact>` composed INLINE; both terms are app
//     facts, so the delegate resolves them into the one boolean the package
//     takes — the severance this same package shipped for the three source-list
//     reads at D2 slice 2, and the `calls/conversations` `isLiveRefreshEnabled`
//     shape before it. Injecting a token hook as well would put a second copy of
//     the same fact on permanent package surface with no consumer, which is the
//     "input nobody consumes" the dictation seam records for the mirror-image
//     case.
//
// The scribe client family is NOT on this seam either: `data/api` reads it from
// the `@heidi/api/client` registry, the same way `data/api/sourceLists.ts`
// already does.
//
// FIVE MORE INPUTS ARRIVED WITH THE CHIP-QUERY CARVE, and they are a different
// KIND of input from `queryClient` rather than more of the same. That hook is a
// REQUEST BUILDER over SSE (`@fortaine/fetch-event-source`), not an axios call,
// so it gets none of the interceptor stack the scribe registry hands every other
// carved read: it assembles the base URL, the bearer, the temp-user id, the
// client-identity headers and the workspace-id header by hand, exactly as
// `scribeHTTP`'s interceptors do for everyone else. Every one of the five is an
// APP FACT — a region resolution, a `localStorage` read, a build-time version, a
// store-backed token read, a jotai store read — and none may enter a headless
// zone. They are getters rather than values because each is read at REQUEST time
// and four of the five can change between the call to `configureEvidenceData`
// and the first chip query.
//
// `getWorkspaceIdHeader` IS ONE OF THE FIVE, and the hand-off said it need not
// be — that a direct `@heidi/workspace/data` import is fine because workspace is
// FOUNDATIONAL (arch §5.3). MEASURED AND WRONG, and the correction is worth
// stating because the two rules read as one and are not. §5.3 governs WHETHER a
// product domain may depend on workspace (it may, with no `DOMAIN_EDGES` entry —
// which is why `evidence: []` stays correct). §5.2 Aperture 1 law 4 governs
// WHICH DOOR: a package `/data` zone may reach a foreign domain through
// `/contracts` only, never its root and never its `/data`. That one is enforced,
// not advisory — `@typescript-eslint/no-restricted-imports` rejects the
// specifier by pattern, and `packages/coding`, the heaviest user of the
// foundational exemption in the repo, imports `@heidi/workspace/contracts` from
// `/data` and `@heidi/workspace/data` only from `/ui`. `getWorkspaceIdHeader` is
// a runtime read of a jotai store, so it cannot live in an inert `/contracts`
// door either. The seam is what is left, and it is also what
// `integrations/document-inbox` already does with `useWorkspaceId`.
//
// THREE MORE ARRIVED WITH THE COLLECTIONS CARVE, and they are a third kind
// again: not transport facts, but the terms every collections and source-list
// READ composes into its `enabled`. Those gates were resolved app-side at a
// bodied delegate until now, which is exactly what the no-shim ruling removes —
// with no old path left to compute them, the terms cross here.
//
// Authentication reads `@heidi/auth/state`. It is not a member of this seam.
//
// The two flag gates cross as THUNKS and are read at the
// same moment the delegate read them — inside the hook body, every render. A
// boolean would freeze the flip.
//
// `useSourceCatalogue` is the app's `useQuerySources` delegate, carrying its
// on-prem `enabled`. It crosses rather than being called package-side because
// `src/utils/backendSurfaces.guard.test.ts` pins that delegate as the ONLY
// importer of the door's `useQuerySources` outside this package — the
// chokepoint that keeps the on-prem surface gate closable.
//
// FOUR MORE ARRIVED WITH THE USER-DOCUMENTS CARVE, and three of them are a
// kind this seam did not carry before: an EFFECT the package fires rather than
// a fact it reads.
//
// `isEvidenceUserDocsEnabled` is the familiar kind — a fourth flag thunk, on
// the same rule as the other two.
//
// `invalidateGamifyTrialExtension` is a commerce invalidation that a document
// upload triggers. It crosses rather than the package calling
// `@heidi/commerce/data` directly, because §5.2 Aperture 1 law 4 lets a
// zone reach a FOREIGN domain through `/contracts` only, and an invalidator is
// not an inert contract. The app COMPOSES it from the two commerce door
// commands — `invalidateGamifyTrialExtension` and
// `invalidateCurrentUserBillingInfo` — which is what the `src/hooks/query`
// composite of the same name calls, so the blast radius is unchanged.
//
// `trackLibraryEvent` and `useTrackChatError` are the analytics pair, and the
// split between them is not stylistic. The first takes a `LibraryAnalyticsEvent`
// (this domain's own vocabulary, declared in `/contracts`) and the app maps each
// member to its `TrackingEventName` and destination options; the second is a
// HOOK because the app's `trackChatError` closes over `useEvidenceAnalytics`'
// base props — the workspace, the region, the session stage — which are
// resolved per render and would freeze if a function were captured at wiring
// time.
import type { QueryClient } from '@tanstack/react-query';
import type { ComponentType } from 'react';

import type {
  ChatSourceItem,
  LibraryAnalyticsEvent,
  LibraryChatErrorReport,
} from '../contracts';

/** Resolves the bearer for the SSE request; may return null/undefined. */
export type EvidenceAuthTokenGetter = () => Promise<string | null | undefined>;

/** What a source picker needs off the catalogue read, and nothing more. */
export interface EvidenceSourceCatalogue {
  data: ChatSourceItem[] | undefined;
  isLoading: boolean;
}

export interface EvidenceDataConfig {
  /**
   * Whether this build asks the backend what Evidence chat can do (MOD-1726,
   * F9). A build fact the app resolves; `false` means no request and an
   * unrestricted answer. Required, so a new wiring cannot leave it out and
   * silently change what cloud does.
   */
  capabilitiesReportedByBackend: boolean;
  /**
   * The app's React Query client singleton.
   *
   * Module-scope cache commands run against it — see the header. Deliberately
   * the CLIENT and not a narrowed port: the two commands need `getQueryData`
   * and `invalidateQueries`, and a structural two-method interface would have to
   * grow every time a carved invalidator needs a third, while buying nothing —
   * `QueryClient` is a `@tanstack/react-query` type, not an app type, so naming
   * it here couples this package to nothing it is not already coupled to.
   */
  queryClient: QueryClient;
  /**
   * A LOADER for the app's async auth-token read, and the indirection is
   * load-bearing — do not "simplify" it to the function itself.
   *
   * `src/utils/evidence-data-wiring.ts` is imported for effect by `_app`, so a
   * static import of `@/utils/http/getAuthTokenForHttpClient` there would
   * evaluate that module's cone at `_app` module scope. The cone reaches
   * `@/store` — an async module under rspack/webpack — and pulling it that early
   * is exactly what produced `ReferenceError: Cannot access 'v' before
   * initialization` during "Collecting page data" on the integrations carve
   * (`src/utils/dictation-data-wiring.ts` records the same trap and the same
   * shape). No local gate sees it: tsc, jest and lint are all blind to
   * module-initialisation order in a bundled chunk, so only a production build
   * catches a regression here.
   *
   * Deferring also restores the pre-carve timing: before the move the hook
   * imported the helper directly, so the cone was evaluated only when a chip
   * query surface mounted.
   *
   * May return the getter or a promise of it, so a test can hand in a plain
   * `() => double` where the app hands in a dynamic `import()`.
   */
  loadAuthToken: () => EvidenceAuthTokenGetter | Promise<EvidenceAuthTokenGetter>;
  /**
   * Client platform + app version headers (`X-Client-Id` / `X-Client-Version`).
   *
   * A build fact resolved from `isTauri()` and the packaged version, so the
   * package cannot derive it. Read per request, matching what the axios
   * interceptor does for every non-SSE call.
   */
  getXClientHeaders: () => Record<string, string>;
  /**
   * The anonymous visitor's id, from `localStorage`.
   *
   * Read per request rather than captured at wiring time: it is written during
   * the anonymous flow, so a value captured at `_app` scope would be `null` for
   * the whole session it exists to identify.
   */
  getTempUserId: () => string | null;
  /**
   * Region-resolved service origins. Only `baseV2Url` is consumed — the SSE URL
   * is built from it — but the port keeps the app's own getter name and return
   * shape rather than a pre-resolved string: the region can change within a
   * session (EHR launch passes one in), so resolving once at wiring time would
   * pin the first region for the life of the tab.
   */
  getDomains: () => { baseV2Url?: string } | null | undefined;
  /**
   * The active workspace-id header, or `{}` when no workspace is selected.
   *
   * The app supplies `getWorkspaceIdHeader` from `@heidi/workspace/data`
   * verbatim — workspace's own documented non-React escape hatch, which exists
   * "because `scribeHTTP.ts` stamps the header at request time and the SSE
   * source does the same, both outside React and synchronous on the hot path".
   * It crosses the seam rather than being imported because §5.2 law 4 lets a
   * `/data` zone reach a foreign domain through `/contracts` only; see the
   * header for why §5.3 does not override that.
   */
  getWorkspaceIdHeader: () => Record<string, string>;
  /**
   * `IS_EVIDENCE_LIBRARY_ENABLED()` — the library gate on every collections
   * and source-list read.
   *
   * A THUNK, never a boolean: a boolean captured at `_app` scope freezes the
   * flip. `boolean | undefined` is the true return and is load-bearing — see
   * `./injected` for the PostHog loading window the `undefined` represents.
   */
  isEvidenceLibraryEnabled: () => boolean | undefined;
  /**
   * `IS_UNIFIED_COLLECTIONS_ENABLED()` — the gate on the unmigrated
   * source-list read and on the unified grid projection. Same thunk rule.
   */
  isUnifiedCollectionsEnabled: () => boolean;
  /**
   * The static source catalogue behind `GET /chat/sources`, already carrying
   * the app's on-prem surface gate.
   *
   * It crosses the seam rather than the package calling its own
   * `useQuerySources` directly, because `backendSurfaces.guard.test.ts` holds
   * `src/hooks/query/useQuerySources.ts` to being that hook's only importer
   * outside this package — the chokepoint that keeps the gate closable. The
   * app binds the delegate here, so the catalogue reaches the pickers with the
   * gate already applied and no second entry point exists.
   */
  useSourceCatalogue: () => EvidenceSourceCatalogue;
  /**
   * `IS_EVIDENCE_USER_DOCS_ENABLED()` — the gate on the user-documents list.
   *
   * A THUNK for the reason the other two are, and `boolean | undefined` for
   * the reason `isEvidenceLibraryEnabled` is: the app getter is a PostHog
   * read, so it is `undefined` while that client loads and the pre-carve gate
   * expression evaluated to `undefined` in that window. react-query reads
   * that as its default — ENABLED — which is live behaviour on every cold
   * load, so nothing here coerces it.
   */
  isEvidenceUserDocsEnabled: () => boolean | undefined;
  /**
   * The app's composite gamify-trial invalidation, fired after a document
   * upload settles.
   *
   * It crosses rather than the package importing `@heidi/commerce/data`,
   * because a `/data` zone may reach a foreign domain through `/contracts`
   * only and an invalidator is not inert.
   *
   * THE APP COMPOSES IT FROM THE TWO COMMERCE DOOR COMMANDS —
   * `invalidateGamifyTrialExtension` and `invalidateCurrentUserBillingInfo`,
   * awaited together — rather than binding the `src/hooks/query` composite
   * that spells the same pair. Same two functions, same order, same blast
   * radius; what the indirection buys is that the wiring module does not
   * become an importer of a `commerce/trials` family subject whose
   * legacy-importer list is shrink-only.
   */
  invalidateGamifyTrialExtension: () => Promise<void>;
  /**
   * Emits one of this domain's six library analytics events.
   *
   * The VOCABULARY is contract (`LibraryAnalyticsEvent` in `/contracts`); the
   * EMIT is an app reaction. The wiring maps each member to
   * `track({ event_name: TrackingEventName.X, props, options })`, carrying the
   * per-event destination options verbatim — three of the six are
   * `{ amplitude: false }` today.
   */
  trackLibraryEvent: (event: LibraryAnalyticsEvent) => void;
  /**
   * The app's chat-error reporter, for the one library surface that uses it.
   *
   * A HOOK rather than a function (ruling 65's shape): the app's
   * `trackChatError` closes over `useEvidenceAnalytics`' per-render base props
   * — workspace, region, session stage, source selection — and a function
   * captured at wiring time would stamp every delete failure with the boot
   * context instead of the current one.
   */
  useTrackChatError: () => (report: LibraryChatErrorReport) => void;
  /**
   * `IS_BULK_UPLOAD_ENABLED()` — whether the upload dropzones accept more
   * than one file at a time. A THUNK, on the rule above.
   */
  isBulkUploadEnabled: () => boolean;
  /**
   * react-pdf's `Document` and `Page`, already wrapped in the app's
   * `ssr: false` dynamic loader.
   *
   * THE WORKER PATH IS WHY THIS CROSSES. `@/lib/pdf/react-pdf-loader` sets
   * `pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'` — an
   * absolute path into the app's `public/`, which the package does not own
   * and cannot serve. Importing `react-pdf` package-side would give the
   * viewer a second pdf.js instance with no worker configured, which fails at
   * the first render rather than at build.
   *
   * Typed as component-ish rather than with react-pdf's own types: naming
   * those here would put `react-pdf` in this package's manifest for a type.
   */
  reactPdf: {
    Document: ComponentType<Record<string, unknown>>;
    Page: ComponentType<Record<string, unknown>>;
  };
  /**
   * The active `Region`, resolved by the app.
   *
   * A GETTER, read where the source-search helpers read it today. The app's
   * resolution walks the Kinde properties cache, the Rematch store, the
   * EHR-launch atom, the widget atom and the host name, and the answer can
   * change within a session (an EHR launch passes one in), so a value
   * captured at wiring time would pin the first region for the tab.
   *
   * Typed as the string the helpers compare against rather than as
   * `@heidi/schema`'s `Region` enum, because the comparison is by value.
   */
  getRegion: () => string;
  /**
   * Writes the clinician's evidence settings.
   *
   * The library upload-risk opt-in keeps its body package-side — the READ is
   * `@heidi/workspace/data`'s `useQueryUser`, which a `/ui` zone may import —
   * but the WRITE is the app's `useMutateUpdateUser`, which is NOT the
   * workspace door's hook of the same name (ruling 65). It crosses bound to
   * `useMutateUpdateUser({ enableInvalidation: false }).mutate`, so the
   * acceptance write still skips the user-query invalidation it always did.
   */
  useUpdateEvidenceSettings: () => (settings: {
    evidence_settings: { library_upload_risk_accepted_at: string };
  }) => void;
}

let config: EvidenceDataConfig | null = null;

export const configureEvidenceData = (next: EvidenceDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getEvidenceDataConfig = (): EvidenceDataConfig => {
  if (!config) {
    throw new Error(
      '[evidence/data] not configured — app bootstrap must call ' +
        'configureEvidenceData() (see src/utils/evidence-data-wiring.ts); ' +
        'tests exercising real evidence data modules that touch the cache ' +
        'commands must configure a test double first.'
    );
  }
  return config;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetEvidenceDataConfigForTests = (): void => {
  config = null;
};
